import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styled, { css } from 'styled-components';
import { Button, Dropdown, Flex } from '../design-system';
import Alert from '../helpers/alert';
import { ERR_MSG } from '../globalConstants';
import useDownload from '../hooks/use-download';

type ExportType = 'clients' | 'contacts' | 'funders' | 'projects' | 'deadlines';

const FILENAMES: Record<ExportType, string> = {
  clients: 'Clients-List-Export.xlsx',
  contacts: 'Contact-List-Export.xlsx',
  funders: 'Funders-List-Export.xlsx',
  projects: 'Project-List-Export.xlsx',
  deadlines: 'Deadlines-List-Export.xlsx',
};

const POLL_INTERVAL = 2500;

const ReadyBanner = styled.div(
  ({ theme }) => css`
    margin-top: 8px;
    padding: 8px 14px;
    border: 1px solid ${theme.colors.green};
    border-radius: 8px;
    background: rgba(0, 165, 58, 0.08);
    font-size: 0.875rem;

    a {
      color: ${theme.colors.green};
      font-weight: 600;
    }
  `,
);

// Export dropdown shared by the Clients, Contacts, Funders, Projects and
// Deadlines list pages. "Download" queues a background job and polls its
// status: the button shows "Preparing export…" while the job runs, then
// becomes a direct download link once the file is ready. "Email me the file"
// keeps the original queued-email behavior.
const ExportButton = ({ type }: { type: ExportType }) => {
  // Set while a queued export is generating (polling) — null when idle.
  const [requestId, setRequestId] = useState<number | null>(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const download = useDownload();

  useEffect(() => {
    if (!requestId || ready) {
      return;
    }

    const poll = async () => {
      try {
        const { data } = await axios.get(`/export/downloads/${requestId}`);
        if (data.status === 'Completed') {
          setReady(true);
        } else if (data.status === 'Failed') {
          Alert.error(ERR_MSG);
          setRequestId(null);
        }
      } catch (error) {
        Alert.error(ERR_MSG);
        setRequestId(null);
      }
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [requestId, ready]);

  const requestExport = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`/export/${type}/download`);
      setRequestId(data.id);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
    setBusy(false);
  };

  const emailExport = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`/export/${type}`);
      Alert.success(data.message);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
    setBusy(false);
  };

  const saveFile = async () => {
    setBusy(true);
    try {
      const { data, headers } = await axios.get(
        `/export/downloads/${requestId}/file`,
        { responseType: 'arraybuffer' },
      );
      download({
        fileName: FILENAMES[type],
        data,
        headers: headers as Record<string, string>,
      });
    } catch (error) {
      Alert.error(ERR_MSG);
    }
    setRequestId(null);
    setReady(false);
    setBusy(false);
  };

  let button: React.ReactNode;

  if (ready) {
    button = (
      <Button variant="green" onClick={saveFile} loading={busy}>
        Download Export
      </Button>
    );
  } else if (requestId) {
    button = (
      <Button variant="green" loading>
        Preparing export…
      </Button>
    );
  } else {
    button = (
      <Dropdown
        trigger={
          <Button variant="green" loading={busy}>
            Export
          </Button>
        }
      >
        <Dropdown.Item onClick={requestExport}>Download</Dropdown.Item>
        <Dropdown.Item onClick={emailExport}>Email me the file</Dropdown.Item>
      </Dropdown>
    );
  }

  return (
    <Flex flexDirection="column" alignItems="flex-end">
      {button}
      {ready && (
        <ReadyBanner>
          Your export is ready:{' '}
          <a href={`/export/downloads/${requestId}/file`}>Download</a>
        </ReadyBanner>
      )}
    </Flex>
  );
};

export default ExportButton;
